const shopifyEndpoint = 'https://bullshift-performance.myshopify.com/api/2026-07/graphql.json';
const shopifyPublicToken = 'ff02b2b9dadc4825344cce1757201bbf';
let selectedCountry = window.localStorage.getItem('midengine-country-code');

function countryContext(countryCode = selectedCountry) {
  return countryCode ? ` @inContext(country: ${countryCode})` : '';
}

async function requestShopify(query, variables) {
  const response = await fetch(shopifyEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': shopifyPublicToken
    },
    body: JSON.stringify({ query, variables })
  });
  const result = await response.json();
  if (!response.ok || result.errors?.length) {
    throw new Error(result.errors?.[0]?.message || `Shopify request failed (${response.status})`);
  }
  return result.data;
}

async function getProducts(first = 8) {
  const products = [];
  let after = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const data = await requestShopify(`
    query Products($first: Int!, $after: String)${countryContext()} {
      products(first: $first, after: $after) { nodes {
        id title handle productType description descriptionHtml
        featuredImage { url(transform: { maxWidth: 600, maxHeight: 600 }) altText }
        images(first: 20) { nodes { url(transform: { maxWidth: 1200, maxHeight: 1200 }) altText } }
        priceRange { minVariantPrice { amount currencyCode } }
        variants(first: 1) { nodes { id availableForSale price { amount currencyCode } compareAtPrice { amount currencyCode } } }
      } pageInfo { hasNextPage endCursor } }
    }
  `, { first, after });
    products.push(...data.products.nodes);
    hasNextPage = data.products.pageInfo.hasNextPage;
    after = data.products.pageInfo.endCursor;
  }

  return products;
}

async function getCollections(first = 50) {
  const collections = [];
  let after = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const data = await requestShopify(`
    query Collections($first: Int!, $after: String)${countryContext()} {
      collections(first: $first, after: $after) {
        nodes {
          id
          title
          handle
          products(first: 100) {
            nodes { id }
            pageInfo { hasNextPage endCursor }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `, { first, after });
    collections.push(...data.collections.nodes);
    hasNextPage = data.collections.pageInfo.hasNextPage;
    after = data.collections.pageInfo.endCursor;
  }

  await Promise.all(collections.map(async collection => {
    let productAfter = collection.products.pageInfo.endCursor;
    let hasMoreProducts = collection.products.pageInfo.hasNextPage;

    while (hasMoreProducts) {
      const data = await requestShopify(`
        query CollectionProducts($handle: String!, $first: Int!, $after: String)${countryContext()} {
          collection(handle: $handle) {
            products(first: $first, after: $after) {
              nodes { id }
              pageInfo { hasNextPage endCursor }
            }
          }
        }
      `, { handle: collection.handle, first: 100, after: productAfter });
      const productsPage = data.collection.products;
      collection.products.nodes.push(...productsPage.nodes);
      productAfter = productsPage.pageInfo.endCursor;
      hasMoreProducts = productsPage.pageInfo.hasNextPage;
    }
  }));

  return collections;
}

async function getProductByHandle(handle) {
  const data = await requestShopify(`
    query ProductByHandle($handle: String!)${countryContext()} {
      product(handle: $handle) {
        id title handle productType description descriptionHtml
        featuredImage { url(transform: { maxWidth: 1200, maxHeight: 1200 }) altText }
        images(first: 20) { nodes { url(transform: { maxWidth: 1600, maxHeight: 1600 }) altText } }
        priceRange { minVariantPrice { amount currencyCode } }
        variants(first: 100) {
          nodes {
            id
            title
            availableForSale
            price { amount currencyCode }
            compareAtPrice { amount currencyCode }
            selectedOptions { name value }
          }
        }
      }
    }
  `, { handle });

  return data.product;
}

async function getLocalization() {
  const data = await requestShopify(`
    query Localization {
      localization {
        country { isoCode currency { isoCode symbol } }
        availableCountries { isoCode name currency { isoCode symbol } }
      }
    }
  `);
  return {
    currentCountry: data.localization.country,
    countries: data.localization.availableCountries
  };
}

let cartId = window.localStorage.getItem('midengine-cart-id');

async function getCart() {
  if (!cartId) return null;

  const data = await requestShopify(
    `query Cart($cartId: ID!)${countryContext()} { cart(id: $cartId) { id totalQuantity buyerIdentity { countryCode } cost { subtotalAmount { amount currencyCode } } lines(first: 100) { nodes { id quantity cost { totalAmount { amount currencyCode } } merchandise { ... on ProductVariant { id title product { title handle featuredImage { url(transform: { maxWidth: 300, maxHeight: 300 }) altText } } } } } } } }`,
    { cartId }
  );

  if (!data.cart) {
    cartId = null;
    window.localStorage.removeItem('midengine-cart-id');
  }

  return data.cart;
}

async function addToCart(variantId, quantity = 1) {
  const lines = [{ merchandiseId: variantId, quantity }];
  const data = cartId
    ? await requestShopify('mutation($cartId: ID!, $lines: [CartLineInput!]!) { cartLinesAdd(cartId: $cartId, lines: $lines) { cart { id totalQuantity lines(first: 100) { nodes { quantity merchandise { ... on ProductVariant { id } } } } } userErrors { message } } }', { cartId, lines })
    : await requestShopify(`mutation($lines: [CartLineInput!])${countryContext()} { cartCreate(input: { lines: $lines, buyerIdentity: { countryCode: ${selectedCountry || 'null'} } }) { cart { id totalQuantity lines(first: 100) { nodes { quantity merchandise { ... on ProductVariant { id } } } } } userErrors { message } } }`, { lines });
  const result = data.cartLinesAdd || data.cartCreate;
  if (result.userErrors.length) throw new Error(result.userErrors[0].message);
  cartId = result.cart.id;
  window.localStorage.setItem('midengine-cart-id', cartId);
  return result.cart;
}

async function setCountry(countryCode) {
  selectedCountry = countryCode || null;
  if (selectedCountry) window.localStorage.setItem('midengine-country-code', selectedCountry);
  else window.localStorage.removeItem('midengine-country-code');

  if (!cartId) return null;

  const data = await requestShopify(
    'mutation($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) { cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) { cart { id } userErrors { message } } }',
    { cartId, buyerIdentity: { countryCode: selectedCountry } }
  );
  const result = data.cartBuyerIdentityUpdate;
  if (result.userErrors.length) throw new Error(result.userErrors[0].message);
  return getCart();
}

async function updateCartLine(lineId, quantity) {
  const data = await requestShopify('mutation($cartId: ID!, $lines: [CartLineUpdateInput!]!) { cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { id totalQuantity cost { subtotalAmount { amount currencyCode } } lines(first: 100) { nodes { id quantity cost { totalAmount { amount currencyCode } } merchandise { ... on ProductVariant { id title product { title handle featuredImage { url(transform: { maxWidth: 300, maxHeight: 300 }) altText } } } } } } } userErrors { message } } }', { cartId, lines: [{ id: lineId, quantity }] });
  const result = data.cartLinesUpdate;
  if (result.userErrors.length) throw new Error(result.userErrors[0].message);
  return result.cart;
}

async function removeCartLine(lineId) {
  const data = await requestShopify('mutation($cartId: ID!, $lineIds: [ID!]!) { cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { id totalQuantity cost { subtotalAmount { amount currencyCode } } lines(first: 100) { nodes { id quantity cost { totalAmount { amount currencyCode } } merchandise { ... on ProductVariant { id title product { title handle featuredImage { url(transform: { maxWidth: 300, maxHeight: 300 }) altText } } } } } } } userErrors { message } } }', { cartId, lineIds: [lineId] });
  const result = data.cartLinesRemove;
  if (result.userErrors.length) throw new Error(result.userErrors[0].message);
  return result.cart;
}

async function getCheckoutUrl() {
  const data = await requestShopify('query($cartId: ID!) { cart(id: $cartId) { checkoutUrl } }', { cartId });
  const checkoutUrl = new URL(data.cart.checkoutUrl);

  if (checkoutUrl.hostname === 'midengineco.com' || checkoutUrl.hostname === 'www.midengineco.com') {
    checkoutUrl.hostname = 'bullshift-performance.myshopify.com';
  }

  return checkoutUrl.toString();
}

function formatMoney(amount, currencyCode) {
  const currencyFormatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode
  });
  const { maximumFractionDigits } = currencyFormatter.resolvedOptions();

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits
  }).format(Number(amount));
}

module.exports = { getProducts, getCollections, getProductByHandle, getLocalization, getCart, addToCart, setCountry, updateCartLine, removeCartLine, getCheckoutUrl, formatMoney };
